import { NgIf } from '@angular/common';
import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { RouterOutlet } from '@angular/router';
import { EnvironmentInjector } from '@next/core-lib/environment';
import { FeatureFlagService } from '@next/core-lib/featureflag';
import { NavigationActions, NavigationItem, ParentNavigationItem } from '@next/core-lib/navigation';
import { UserSelectors } from '@next/core-lib/user';
import { AgrWrapperSiteSidenavModule } from '@next/core-lib/wrapper-site-sidenav';
import { Store } from '@ngrx/store';
import { Subject, filter, switchMap, takeUntil, tap, combineLatest, map } from 'rxjs';

import { Environment } from 'src/environments/environment.model';

import { InformationBannerComponent } from './information-banner/information-banner.component';
import { ShopFeatureFlags } from './shared/shop-feature-flags';
import { selectRepeatOrder } from './store/selectors';

interface RepeatOrders {
  bulkCount: number;
  generalCargoCount: number;
}

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  imports: [AgrWrapperSiteSidenavModule, NgIf, InformationBannerComponent, RouterOutlet],
})
export class AppComponent implements OnInit, OnDestroy {
  showSapMaintenanceWarning = false;
  repeatOrders: RepeatOrders = { bulkCount: 0, generalCargoCount: 0 };
  featureFlags: string[] = [];
  private readonly destroy$: Subject<void> = new Subject<void>();

  constructor(
    private readonly store: Store,
    @Inject(EnvironmentInjector) public environment: Environment,
    private featureFlagService: FeatureFlagService,
  ) {}
  ngOnInit(): void {
    combineLatest([this.store.select(selectRepeatOrder)])
      .pipe(
        map(([{ bulk, generalCargo }]) => ({
          bulkCount: bulk.length,
          generalCargoCount: generalCargo.length,
        })),
        takeUntil(this.destroy$),
      )
      .subscribe((repeatOrders) => {
        this.repeatOrders = repeatOrders;
        this.store.dispatch(
          NavigationActions.setNavigationItems({
            navigationItems: this.createNavigationItems(),
          }),
        );
      });
    this.store
      .select(UserSelectors.selectCustomer)
      .pipe(
        filter((selectedCustomer) => selectedCustomer?.shipTos[0]?.salesAreas[0]?.salesOrganization !== undefined),
        switchMap((selectedCustomer) => {
          const salesOrganization = selectedCustomer?.shipTos[0]?.salesAreas[0]?.salesOrganization;
          return this.featureFlagService.getFeatureFlagsForSalesArea(salesOrganization);
        }),
        tap((featureFlags: string[]) => {
          this.showSapMaintenanceWarning = featureFlags.includes(ShopFeatureFlags.showSapMaintenanceWarning);
          this.featureFlags = featureFlags;
        }),
        takeUntil(this.destroy$),
      )
      .subscribe();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private createNavigationItems(): ParentNavigationItem[] {
    const repeatOrdersChildrenNavigationItems: NavigationItem[] = [];
    const navigationItems: ParentNavigationItem[] = [
      {
        relativeUrl: '/shop',
        label: 'app.pages.shop',
        icon: 'shopping_cart',
      },
    ];

    navigationItems.push({
      relativeUrl: '/orders',
      label: 'app.pages.orders',
      icon: 'local_shipping',
    });

    navigationItems.push({
      relativeUrl: '/silos',
      label: 'app.pages.silos',
      icon: 'home',
    });

    if (this.repeatOrders.bulkCount !== 0) {
      repeatOrdersChildrenNavigationItems.push({
        relativeUrl: '/repeat-order/bulk',
        label: 'app.pages.repeat-order-bulk',
      });
    }

    if (this.repeatOrders.generalCargoCount !== 0) {
      repeatOrdersChildrenNavigationItems.push({
        relativeUrl: '/repeat-order/packaged-goods',
        label: 'app.pages.repeat-order-packaged',
      });
    }

    if (repeatOrdersChildrenNavigationItems.length > 0) {
      navigationItems.push({
        label: 'app.pages.repeat-order',
        icon: 'add_shopping_cart',
        children: repeatOrdersChildrenNavigationItems,
      });
    }

    navigationItems.push({
      label: 'app.pages.terms-and-conditions',
      relativeUrl: '/terms-and-conditions',
      icon: 'description',
    });

    navigationItems.push({
      relativeUrl: '/preferences',
      label: 'app.pages.preferences',
      icon: 'notifications',
    });
    return navigationItems;
  }
}
